import { useEffect, useState, useRef } from 'react'
import { getLatestMonthSummary, listPersonSummary } from '@services/payrollService'
import MultiSelectAutocomplete from '@components/ui/MultiSelectAutocomplete'
import Card from '@components/ui/Card'
import { useGlobalFilters } from '@contexts/FilterContext'
import { useFilteredDimensions } from '@hooks/useFilteredDimensions'
import { Wallet, Receipt, PiggyBank, Users } from 'lucide-react'

export default function DashboardPage() {
  const [summary, setSummary] = useState(null)
  const [people, setPeople] = useState({ results: [], count: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const {
    monthYear,
    setMonthYear,
    pendingFilters,
    setPendingFilters,
    appliedFilters,
    applyFilters,
    clearFilters,
    hasUnappliedChanges,
    indicator,
    setIndicator
  } = useGlobalFilters()
  const [monthLocal, setMonthLocal] = useState('')
  const [monthError, setMonthError] = useState('')
  const [allGrupos, setAllGrupos] = useState([])
  const [allSecretarias, setAllSecretarias] = useState([])

  // Dimensões baseadas nos filtros JÁ APLICADOS (para o dashboard e lista completa de grupos)
  const { dimensions: appliedDims } = useFilteredDimensions(appliedFilters, monthYear)

  // TEMPORARIAMENTE DESABILITADO para testar se o loop é causado pelo preview
  // Dimensões de PREVIEW: usa os filtros aplicados + grupo pendente para cascade em tempo real
  // Secretaria NÃO faz parte do preview pois é um filtro independente
  // useMemo para estabilizar a referência e evitar loops infinitos
  // const previewFilters = useMemo(() => ({
  //   ...appliedFilters,
  //   grupo: pendingFilters.grupo || []
  // }), [appliedFilters, pendingFilters.grupo])
  // const { dimensions: previewDims } = useFilteredDimensions(previewFilters, monthYear)

  // Usando appliedDims como fallback temporário
  const previewDims = appliedDims

  // Ref para acessar sempre a versão mais recente de previewDims sem causar re-renders
  const previewDimsRef = useRef(previewDims)
  useEffect(() => {
    previewDimsRef.current = previewDims
  }, [previewDims])

  // Carrega a lista completa de grupos apenas uma vez (sem filtros)
  useEffect(() => {
    if (appliedDims.grupos && appliedDims.grupos.length > 0 && allGrupos.length === 0) {
      setAllGrupos(appliedDims.grupos)
    }
  }, [appliedDims.grupos, allGrupos.length])

  // Carrega a lista completa de secretarias apenas uma vez (sem filtros)
  useEffect(() => {
    if (appliedDims.secretarias && appliedDims.secretarias.length > 0 && allSecretarias.length === 0) {
      setAllSecretarias(appliedDims.secretarias.map(s => s.secretaria))
    }
  }, [appliedDims.secretarias, allSecretarias.length])

  //Limpa filtros dependentes quando grupos mudam
  useEffect(() => {
    const gruposSelecionados = pendingFilters.grupo || []

    if (gruposSelecionados.length === 0) {
      setPendingFilters(prev => ({
        ...prev,
        situacao: [],
        cargo: [],
        fundo: [],
        regime: [],
        grupo_evento: [],
        evento: []
        // Secretaria NÃO é limpa - é um filtro independente
      }))
      return
    }
    // Usa a ref para acessar as dimensões mais recentes sem causar re-renders
    const dims = previewDimsRef.current
    const limparSeInvalido = (chave, opcoesDisponiveis) => {
      const valoresAtuais = pendingFilters[chave] || []
      const valoresValidos = valoresAtuais.filter(v =>
        Array.isArray(opcoesDisponiveis)
          ? opcoesDisponiveis.includes(v)
          : false
      )

      return valoresValidos.length !== valoresAtuais.length ? valoresValidos : null
    }

    // Coleta todas as mudanças primeiro, depois atualiza uma vez só
    const updates = {}
    const situacaoUpdate = limparSeInvalido('situacao', dims.situacoes || [])
    if (situacaoUpdate !== null) updates.situacao = situacaoUpdate

    const cargoUpdate = limparSeInvalido('cargo', dims.cargos || [])
    if (cargoUpdate !== null) updates.cargo = cargoUpdate

    const fundoUpdate = limparSeInvalido('fundo', dims.fundos || [])
    if (fundoUpdate !== null) updates.fundo = fundoUpdate

    const regimeUpdate = limparSeInvalido('regime', dims.regimes || [])
    if (regimeUpdate !== null) updates.regime = regimeUpdate

    const grupoEventoUpdate = limparSeInvalido('grupo_evento', dims.grupos_evento || [])
    if (grupoEventoUpdate !== null) updates.grupo_evento = grupoEventoUpdate

    const eventoUpdate = limparSeInvalido('evento', dims.descricoes_evento || [])
    if (eventoUpdate !== null) updates.evento = eventoUpdate

    // Só atualiza se houver mudanças
    if (Object.keys(updates).length > 0) {
      setPendingFilters(prev => ({ ...prev, ...updates }))
    }

    // Secretaria é INDEPENDENTE e não deve ser validada com base em grupos
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFilters.grupo])

  // Carrega os dados do dashboard (summary e pessoas)
  useEffect(() => {
    const abort = new AbortController()
    let active = true
    async function load() {
      try {
        setLoading(true)
        const query = {}
        Object.entries(appliedFilters).forEach(([k, arr]) => {
          if (Array.isArray(arr) && arr.length) query[k] = arr
        })
        if (monthYear) query['month_year'] = monthYear
        const [s, p] = await Promise.all([
          getLatestMonthSummary(abort.signal, query),
          listPersonSummary({ page: 1, page_size: 10, ...query }, abort.signal)
        ])
        if (!active) return
        setSummary(s)
        setPeople(p)
        if (!monthYear && s?.month_year) setMonthYear(s.month_year)
      } catch (e) {
        if (abort.signal.aborted || e?.name === 'AbortError') return
        if (!active) return
        setError(e.message)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
      abort.abort()
    }
  }, [appliedFilters, monthYear, setMonthYear])

  useEffect(() => {
    setMonthLocal(monthYear || '')
    setMonthError('')
  }, [monthYear])

  const handleMonthChange = (val) => {
    setMonthLocal(val)
    const valid = /^\d{4}-(0[1-9]|1[0-2])$/.test(val)
    if (!val || valid) setMonthError('')
    else setMonthError('Formato inválido. Use YYYY-MM')
  }

  const handleMonthBlur = () => {
    if (!monthLocal) return
    const valid = /^\d{4}-(0[1-9]|1[0-2])$/.test(monthLocal)
    if (valid) setMonthYear(monthLocal)
  }

  if (loading) return (
    <div className="section section-space">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="card-glass p-6">
            <div className="h-4 w-24 skeleton"></div>
            <div className="h-7 w-40 mt-3 skeleton"></div>
          </div>
        ))}
      </div>
    </div>
  )

  if (error) return <div className="p-4 text-red-600">Erro: {error}</div>

  return (
    <div className="section section-space space-y-6">
      <div>
        <h2 className="text-2xl font-semibold title-gradient">Dashboard</h2>
        <p className="text-gray-600">Base atual: {summary?.month_year || '—'}</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Mês/Ano</span>
            </div>
            <input
              type="month"
              className={`w-full rounded-lg border ${monthError ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary`}
              value={monthLocal}
              onChange={(e) => handleMonthChange(e.target.value)}
              onBlur={handleMonthBlur}
            />
            {monthError && <p className="text-xs text-red-600 mt-1">{monthError}</p>}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Indicador</span>
            </div>
            <select
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              value={indicator}
              onChange={(e) => setIndicator(e.target.value)}
            >
              <option value="gross">Folha Bruta (total)</option>
              <option value="net">Folha Líquida (total)</option>
              <option value="avg_gross">Salário Médio Bruto</option>
              <option value="avg_net">Salário Médio Líquido</option>
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Secretaria</span>
            </div>
            <MultiSelectAutocomplete
              label=""
              placeholder="Selecione uma ou mais secretarias"
              options={allSecretarias}
              value={pendingFilters.secretaria || []}
              onChange={(v) => setPendingFilters(prev => ({ ...prev, secretaria: v }))}
            />
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Grupo</span>
            </div>
            <MultiSelectAutocomplete
              label=""
              placeholder="Selecione um ou mais grupos"
              options={allGrupos}
              value={pendingFilters.grupo || []}
              onChange={(v) => setPendingFilters(prev => ({ ...prev, grupo: v }))}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Situação</span>
            </div>
            <MultiSelectAutocomplete
              label=""
              placeholder={pendingFilters.grupo?.length === 0 ? "Selecione um grupo primeiro" : "Selecione..."}
              options={previewDims.situacoes || []}
              value={pendingFilters.situacao || []}
              onChange={(v) => setPendingFilters(prev => ({ ...prev, situacao: v }))}
              disabled={pendingFilters.grupo?.length === 0}
            />
          </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Cargo</span>
          </div>
          <MultiSelectAutocomplete
            label=""
            placeholder={pendingFilters.grupo?.length === 0 ? "Selecione um grupo primeiro" : "Selecione..."}
            options={previewDims.cargos || []}
            value={pendingFilters.cargo || []}
            onChange={(v) => setPendingFilters(prev => ({ ...prev, cargo: v }))}
            disabled={pendingFilters.grupo?.length === 0}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Fundo</span>
          </div>
          <MultiSelectAutocomplete
            label=""
            placeholder={pendingFilters.grupo?.length === 0 ? "Selecione um grupo primeiro" : "Selecione..."}
            options={previewDims.fundos || []}
            value={pendingFilters.fundo || []}
            onChange={(v) => setPendingFilters(prev => ({ ...prev, fundo: v }))}
            disabled={pendingFilters.grupo?.length === 0}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Regime</span>
          </div>
          <MultiSelectAutocomplete
            label=""
            placeholder={pendingFilters.grupo?.length === 0 ? "Selecione um grupo primeiro" : "Selecione..."}
            options={previewDims.regimes || []}
            value={pendingFilters.regime || []}
            onChange={(v) => setPendingFilters(prev => ({ ...prev, regime: v }))}
            disabled={pendingFilters.grupo?.length === 0}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Grupo do Evento</span>
          </div>
          <MultiSelectAutocomplete
            label=""
            placeholder={pendingFilters.grupo?.length === 0 ? "Selecione um grupo primeiro" : "Selecione..."}
            options={previewDims.grupos_evento || []}
            value={pendingFilters.grupo_evento || []}
            onChange={(v) => setPendingFilters(prev => ({ ...prev, grupo_evento: v }))}
            disabled={pendingFilters.grupo?.length === 0}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Evento</span>
          </div>
          <MultiSelectAutocomplete
            label=""
            placeholder={pendingFilters.grupo?.length === 0 ? "Selecione um grupo primeiro" : "Selecione..."}
            options={previewDims.descricoes_evento || []}
            value={pendingFilters.evento || []}
            onChange={(v) => setPendingFilters(prev => ({ ...prev, evento: v }))}
            disabled={pendingFilters.grupo?.length === 0}
          />
        </div>
        </div>
        <div className="mt-4 flex gap-3 items-center">
          <button
            onClick={applyFilters}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              hasUnappliedChanges
                ? 'bg-primary text-white hover:bg-primary-dark'
                : 'bg-gray-300 text-gray-600 cursor-not-allowed'
            }`}
            disabled={!hasUnappliedChanges}
          >
            Aplicar Filtros
          </button>
          <button
            onClick={clearFilters}
            className="px-4 py-2 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
          >
            Limpar Filtros
          </button>
          {hasUnappliedChanges && (
            <span className="text-sm text-orange-600 italic">Filtros não aplicados</span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" /> Salário Bruto
            </p>
          </div>
          <p className="text-2xl font-bold mt-1 text-primary">
            R$ {summary?.gross_total?.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </p>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-rose-500" /> Descontos
            </p>
          </div>
          <p className="text-2xl font-bold mt-1 text-primary">
            R$ {summary?.deductions_total?.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </p>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <PiggyBank className="w-4 h-4 text-emerald-600" /> Salário Líquido
            </p>
          </div>
          <p className="text-2xl font-bold mt-1 text-primary">
            R$ {summary?.net_total?.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </p>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-600" /> Pessoas
            </p>
          </div>
          <p className="text-2xl font-bold mt-1 text-primary">
            {summary?.employee_count?.toLocaleString('pt-BR')}
          </p>
        </Card>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title={`Top Grupos por ${indicator === 'gross' ? 'Bruto' : indicator === 'avg_gross' ? 'Médio Bruto' : indicator === 'avg_net' ? 'Médio Líquido' : 'Líquido'}`}>
          <div className="max-h-[560px] overflow-y-auto pr-2">
            <ul className="divide-y">
              {summary?.by_group?.map((g) => (
                <li key={g.group} className="py-2 flex justify-between">
                  <div className="flex flex-col">
                    <span>{g.group || '—'}</span>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border border-primary text-primary bg-transparent">
                        {g.employees} pessoas
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border border-primary text-primary bg-transparent">
                        {g.employees_pct}%
                      </span>
                    </div>
                  </div>
                  <span className="font-medium">
                    {indicator === 'gross' && `R$ ${g.gross.toLocaleString('pt-BR')}`}
                    {indicator === 'net' && `R$ ${g.net.toLocaleString('pt-BR')}`}
                    {indicator === 'avg_gross' && `R$ ${g.avg_gross.toLocaleString('pt-BR')}`}
                    {indicator === 'avg_net' && `R$ ${g.avg_net.toLocaleString('pt-BR')}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Card>
        <Card title={`Top Cargos por ${indicator === 'gross' ? 'Bruto' : indicator === 'avg_gross' ? 'Médio Bruto' : indicator === 'avg_net' ? 'Médio Líquido' : 'Líquido'}`}>
          <div className="max-h-[560px] overflow-y-auto pr-2">
            <ul className="divide-y">
              {summary?.by_cargo?.map((g) => (
                <li key={g.cargo} className="py-2 flex justify-between">
                  <div className="flex flex-col">
                    <span>{g.cargo || '—'}</span>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border border-primary text-primary bg-transparent">
                        {g.employees} pessoas
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border border-primary text-primary bg-transparent">
                        {g.employees_pct}%
                      </span>
                    </div>
                  </div>
                  <span className="font-medium">
                    {indicator === 'gross' && `R$ ${g.gross.toLocaleString('pt-BR')}`}
                    {indicator === 'net' && `R$ ${g.net.toLocaleString('pt-BR')}`}
                    {indicator === 'avg_gross' && `R$ ${g.avg_gross.toLocaleString('pt-BR')}`}
                    {indicator === 'avg_net' && `R$ ${g.avg_net.toLocaleString('pt-BR')}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      </div>
    </div>
  )
}